import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges, input, computed } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { PrismStatusIndicatorComponent, PrismTooltipComponent } from '@shure/prism-angular-components';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { provideInlineScope } from '@shure/shared/angular/utils/i18n';

import { DeviceImageProvider } from './device-image.provider';

@Component({
	selector: 'sh-device-image',
	templateUrl: './device-image.component.html',
	styleUrls: ['./device-image.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [CommonModule, PrismStatusIndicatorComponent, TranslocoModule, PrismTooltipComponent],
	providers: [provideInlineScope('scDeviceImage', (lang) => import(`../i18n/${lang}`))]
})
export class DeviceImageComponent implements OnChanges {
	/**
	 * Determines if the device is in virtual/live mode
	 */
	@Input()
	public isVirtual = false;

	/**
	 * Determines if the device is offline
	 */
	@Input()
	public isOffline = false;

	/**
	 * Determines if the device has incompatible firmware
	 */
	@Input()
	public incompatibleFirmware = false;

	/**
	 * Determines the device model for the image to be displayed
	 */
	@Input() public deviceModel!: DeviceModel | undefined;

	/**
	 * Determines if the device is password protected
	 */
	@Input() public isPasswordEnabled!: boolean | undefined;

	/**
	 * Determines if the locked device was successfully authenticated
	 * It is set to false, until we get full details on the authentication process.
	 */
	@Input() public isAuthenticated = false;

	/**
	 * Determines the number of channels
	 * Used for devices that show the channel count in the image
	 */
	@Input() public channelCount: number | undefined;

	/**
	 * Disable the tooltip of the image (default false)
	 */
	@Input() public disableTooltip = false;

	/**
	 * Displays linking status for supporting devices
	 */
	public linked = input<boolean | undefined>(undefined);

	/**
	 * Displays warning icon if fw is not matching associated/linked devices
	 */
	public isFirmwareMismatch = input<boolean | undefined>(undefined);

	/**
	 * Path for the device image to be rendered
	 *
	 * @ignore internal
	 */
	public backgroundImage!: string;

	public readonly linkedStatusIcon = computed(() => {
		if (this.isFirmwareMismatch()) {
			return './assets/images/dockable-transmitter-status/unknown-link.svg';
		}
		if (this.linked()) {
			return './assets/images/dockable-transmitter-status/linked.svg';
		}
		return './assets/images/dockable-transmitter-status/unlinked.svg';
	});

	constructor(
		private readonly provider: DeviceImageProvider,
		private readonly translocoService: TranslocoService
	) {}

	/**
	 * On one of the input properties change, calls the deviceImageService to get the path for the device image to be rendered
	 */
	public ngOnChanges(_changes: SimpleChanges): void {
		if (this.deviceModel) {
			const imageInfo = this.provider.getImage(this.deviceModel, this.channelCount);
			const imageWithPath = this.isVirtual ? imageInfo.virtual : imageInfo.live;
			this.backgroundImage = `url(${imageWithPath})`;
		}
	}

	public getDeviceTooltip(): string {
		if (!this.isVirtual && this.isPasswordEnabled) {
			return `${this.deviceModel}: ${
				this.isAuthenticated
					? this.translocoService.translate('scDeviceImage.unlocked')
					: this.translocoService.translate('scDeviceImage.locked')
			}`;
		}
		return this.deviceModel ?? '';
	}
}
